import React, { Suspense, lazy } from 'react';
import { Router, Switch, Route, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';

import store from 'store/store';

import UrlHelper from 'utils/helper/url-helper';
import ErrorHelper from 'utils/helper/error-helper';

import routes from 'resources/routes.json';
import CustomerHomePage from 'pages/logged-area/home/home';
import history from './history';

import MainComponent from '../main-component';
import InsuranceOfferPage from '../pages/application/insurance-offer-page/insurance-offer-page';
import BankInfoUpdateNew from '../pages/application/bank-info-update/bank-info-update-new';
import PendingAcceptableBank from '../pages/application/pending-acceptable-bank';
import CounterOfferProcess from '../pages/application/contract/counter-offer/process/index';
import DueDateUpdate from '../pages/application/due-date-update/due-date-update';
import SendLoginLink from '../pages/logged-area/customer-login';

const { lazyRetry } = ErrorHelper;

// Application
const PreContract = lazy(() => lazyRetry(() => import('pages/application/pre-contract/pre-contract')));
const PostContract = lazy(() => lazyRetry(() => import('pages/application/post-contract/post-contract')));
const DeclinedComponent = lazy(() => lazyRetry(() => import('pages/application/declined/declined')));
const Approved = lazy(() => lazyRetry(() => import('pages/application/approved/approved')));
const ApprovedPending = lazy(() => lazyRetry(() => import('pages/application/approved/approved-disbursement-fail')));
const DeclinedBankComponent = lazy(() => lazyRetry(() => import('pages/application/declined/declined-bank')));
const Underwriting = lazy(() => lazyRetry(() => import('pages/application/underwriting/underwriting')));
const IncomeProofIassessementsEbsPage = lazy(() =>
  lazyRetry(() => import('pages/application/income-proof/income-proof-iassessements-ebs')),
);
const IncomeProofIassessementsPage = lazy(() =>
  lazyRetry(() => import('pages/application/income-proof/income-proof-iassessements')),
);
const OpenFinance = lazy(() => lazyRetry(() => import('pages/application/income-proof/open-finance')));
const StartOpenFinance = lazy(() => lazyRetry(() => import('pages/application/income-proof/start-open-finance')));
const OpenFinanceFaq = lazy(() => lazyRetry(() => import('pages/application/income-proof/open-finance-faq')));
const TokenValidationPage = lazy(() =>
  lazyRetry(() => import('pages/application/token-validation-page/token-validation-page')),
);
const Contract = lazy(() => lazyRetry(() => import('pages/application/contract/contract')));
const MultiOfferPage = lazy(() =>
  lazyRetry(() => import('pages/application/contract/multioffer-selection/multioffer-selection')),
);
const ContractExtraFormalization = lazy(() =>
  lazyRetry(() => import('pages/application/contract/extra-formalization/extra-formalization')),
);
const EbsPage = lazy(() => lazyRetry(() => import('pages/application/contract/counter-offer/ebs-page')));
const PendingDataSources = lazy(() =>
  lazyRetry(() => import('pages/application/contract/pending-data/pending-data-sources')),
);
const PagePixPayment = lazy(() => lazyRetry(() => import('pages/application/page-pix-payment/page-pix-payment')));
const LeadComplement = lazy(() => lazyRetry(() => import('pages/application/lead-page/lead-complement-application')));
const AppInstallationPage = lazy(() => lazyRetry(() => import('pages/application/app-installation/app-installation')));
const LeadPage = lazy(() => lazyRetry(() => import('pages/application/lead-page/lead-application')));
const PageAprovaSim = lazy(() => lazyRetry(() => import('pages/application/page-aprovasim/page-aprova-sim.jsx')));
const SignInsurance = lazy(() => lazyRetry(() => import('pages/logged-area/sign-insurance/sign-insurance')));
const LeadPageCpc = lazy(() => lazyRetry(() => import('pages/application/lead-page/lead-application-cpc')));
const KbaPage = lazy(() => lazyRetry(() => import('pages/application/kba-page/kba-page')));
const InsurancePage = lazy(() => lazyRetry(() => import('pages/logged-area/insurance/insurance')));
const PageAdiantaSim = lazy(() => lazyRetry(() => import('pages/application/page-adiantasim/page-adianta-sim.jsx')));
const MultiplikIndication = lazy(() =>
  lazyRetry(() => import('pages/application/multiplik-indication/multiplik-indication')),
);
const LeadPageBancoPan = lazy(() =>
  lazyRetry(() => import('pages/application/bancopan-indication/banco-pan-indication')),
);
const IdentityProof = lazy(() => lazyRetry(() => import('pages/application/biometry/identity-proof')));
const DataCaptureAppInstallationPage = lazy(() =>
  lazyRetry(() => import('pages/application/data-capture-app/data-capture-app-installation')),
);

const LoadingPage = lazy(() => lazyRetry(() => import('pages/application/loading-page/loading-page')));
const LoadingPageDisbursement = lazy(() => lazyRetry(() => import ('pages/application/loading-page/loading-page-disbursement')));
// Customer
// const CustomerHomePage = lazy(() => lazyRetry() () => import('pages/logged-area/home/home'));
const CustomerLoanPage = lazy(() => lazyRetry(() => import('pages/logged-area/loan/loan')));
const CustomerShopPage = lazy(() => lazyRetry(() => import('pages/logged-area/supersimshop/supersimshop')));
const CustomerShopPromotionPage = lazy(() =>
  lazyRetry(() => import('pages/logged-area/supersimshop/promotion-detail')),
);
const Evaluation = lazy(() => lazyRetry(() => import('pages/logged-area/evaluation/evaluation')));
const CustomerArea = lazy(() => lazyRetry(() => import('pages/logged-area/customer-area/customer-area')));

// Others
const PageNotFoundComponent = lazy(() => lazyRetry(() => import('pages/application/page-not-found/page-not-found')));
const Login = lazy(() => lazyRetry(() => import('pages/logged-area/login/login')));
const RequestPasswordReset = lazy(() =>
  lazyRetry(() => import('pages/logged-area/request-password-reset/request-password-reset')),
);
const MessageOpsOut = lazy(() => lazyRetry(() => import('pages/application/message-ops-out/message-ops-out')));
const LoginRedirect = lazy(() => lazyRetry(() => import('pages/logged-area/login-redirect/login-redirect')));
const LoginLink = lazy(() => lazyRetry(() => import('pages/logged-area/login-link/login-link')));
const PasswordResetContainer = lazy(() => lazyRetry(() => import('pages/logged-area/reset-password/reset-password')));
const StatusCardPayment = lazy(() =>
  lazyRetry(() => import('components/features/card-payment/status-card-payment-component')),
);
const SingleCardPaymentComponent = lazy(() =>
  lazyRetry(() => import('components/features/card-payment/single-card-payment-component')),
);

const FormalizationFailedBankAccount = lazy(() =>
  lazyRetry(() => import('pages/application/formalization-failed/formalization-failed-bank-account.jsx')),
);

const BankAccountInfoFailed  = lazy(() =>
  lazyRetry(() => import('pages/application/bank-info-update/bank-info-update-fail.jsx')),
);

const LoadingOpenFinance = lazy(() =>
  lazyRetry(() => import('components/features/credigo-frame/loading-open-finance')),
);

const ProcessingOpenFinance = lazy(() =>
  lazyRetry(() => import('pages/application/income-proof/processing-open-finance')),
);

const BankInfoFaq = lazy(() => lazyRetry(() => import('pages/application/bank-info-update/bank-info-faq')));
const BankInfoFail = lazy(() => lazyRetry(() => import('pages/application/bank-info-update/bank-info-fail')));

const renderLoader = () => <> </>;

export default function App() {
  return (
    <Provider store={store}>
      <Router history={UrlHelper.removeR(history)}>
        <Suspense fallback={renderLoader()}>
          <MainComponent>
            <Switch>
              <Route exact path="/">
                <Redirect path="/" to={routes.LOAN_APPLICATION_START.DEFAULT} />
              </Route>

              {/* Collection - Payment By Card Page  */}
              <Route
                exact
                path={UrlHelper.getAllRoutes(routes.SINGLE_CARD_PAYMENT)}
                component={SingleCardPaymentComponent}
              />
              <Route exact path={UrlHelper.getAllRoutes(routes.STATUS_CARD_PAYMENT)} component={StatusCardPayment} />

              {/* Funnel - Lead  */}
              <Route
                exact
                path={[
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_LEAD),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_SERASA),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_SHORT_APPLICATION),
                ]}
              >
                <LeadPage />
              </Route>

              <Route
                exact
                path={[
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_CPC),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_TL),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_MULTIPLIK_BV),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_AD),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_ULLOO),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_VOLTZ),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_HK),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_TBA),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_IQ),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_PARTNER_500),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_IG),
                ]}
              >
                <LeadPageCpc />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_BANCO_PAN)}>
                <LeadPageBancoPan />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_MULTIPLIK)}>
                <MultiplikIndication />
              </Route>

              {/* Funnel - Complement  */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_START_COMPLEMENT)}>
                <LeadComplement />
              </Route>

              {/* Funnel - Pre Contract  */}
              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_PRE_CONTRACT)]}>
                <PreContract />
              </Route>

              {/* Application - Data sources still being processed */}
              <Route exact path={[UrlHelper.getAllRoutes(routes.APPLICATION_PENDING_CREDIT_POLICY)]}>
                <PendingDataSources />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_PENDING_TOKEN_CLICK)]}>
                <TokenValidationPage />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_CONTRACT)]}>
                <Contract />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_CONTRACT_EXTRA_FORMALIZATION)]}>
                <ContractExtraFormalization />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_MULTIOFFER)]}>
                <MultiOfferPage />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_CONTRACT_EBS_WITH_COUNTER_OFFER)]}>
                <EbsPage showCounterOffer />
              </Route>

              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_CONTRACT_EBS_WITHOUT_COUNTER_OFFER)]}>
                <EbsPage showCounterOffer={false} />
              </Route>

              {/* Funnel - Post Contract */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_POST_CONTRACT)}>
                <PostContract />
              </Route>

              {/* Funnel - Underwriting Options */}

              {/* KBA  */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_KBA_VERIFICATION)}>
                <KbaPage />
              </Route>

              {/* Income Proof and Open Finance Consent */}
              <Route
                exact
                path={[
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME_PROF),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME_CREDIGO),
                  UrlHelper.getAllRoutes(routes.LOAN_EBS),
                  UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME_EBS),
                  UrlHelper.getAllRoutes(routes.CUSTOMER_OPEN_FINANCE),
                ]}
              >
                <OpenFinance />
              </Route>

               {/* Pending acceptable bank */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_PENDING_ACCEPTABLE_BANK)}>
                <PendingAcceptableBank />
              </Route>

              {/* Open Finance FAQ */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_OPEN_FINANCE_FAQ)}>
                <OpenFinanceFaq />
              </Route>

              {/* Open Finance START */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_START_OPEN_FINANCE)}>
                <StartOpenFinance />
              </Route>

              {/* Biometry */}
              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_UNICO_IDENTITY)]}>
                <IdentityProof />
              </Route>

              {/* Iassessments + EBS */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME_PROF_IA_EBS)}>
                <IncomeProofIassessementsEbsPage />
              </Route>

              {/* Loading Open finance */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOADING_OPEN_FINANCE)}>
                <LoadingOpenFinance />
              </Route>

              {/* Processing Open finance*/}
              <Route exact path={UrlHelper.getAllRoutes(routes.PROCESSING_OPEN_FINANCE)}>
                <ProcessingOpenFinance />
              </Route>

              {/* Iassessments */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_INCOME_PROF_IA)}>
                <IncomeProofIassessementsPage />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_APP_INSTALLATION)}>
                <AppInstallationPage />
              </Route>

              {/* AprovaSim */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_APROVASIM)}>
                <PageAprovaSim />
              </Route>

              {/* AdiantaSim */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_ADIANTASIM)}>
                <PageAdiantaSim />
              </Route>

              {/* Short Application */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_ADIANTASIM_SHORT_APPLICATION)}>
                <Redirect to="/jornada/oferta" />
              </Route>

              {/* Funnel - Declined */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_DECLINED)}>
                <DeclinedComponent />
              </Route>

              {/* Funnel - Declined Bank */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_DECLINED_BANK)}>
                <DeclinedBankComponent />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_UPDATE_BANK_ACCOUNT_DISBURSEMENT_FAILED)}>
                <BankAccountInfoFailed/>
              </Route>

              {/* Funnel - Declined Bank */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_DISBURSEMENT_FAILED_BANK_ACCOUNT)}>
                <FormalizationFailedBankAccount />
              </Route>

              {/* Funnel - Underwriting Final Stage */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_UNDERWRITING)}>
                <Underwriting />
              </Route>

              {/* Funnel - Approved */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPROVED)}>
                <Approved />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_APPROVED_PENDING)}>
                <ApprovedPending />
              </Route>

              {/* Funnel - Insurance offer page */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_INSURANCE_OFFER_PAGE)}>
                <InsuranceOfferPage />
              </Route>

              {/* Customer Area */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_PAGE)}>
                <CustomerArea>
                  <CustomerHomePage />
                </CustomerArea>
              </Route>
              {/* Por enquanto estou mantendo a URL sumario para evitar 404 */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_CUSTOMER_SUMMARY)}>
                <CustomerArea>
                  <CustomerHomePage />
                </CustomerArea>
              </Route>

              {/* Customer Area - Active Loan Page */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_LOAN_PAGE)}>
                <CustomerArea>
                  <CustomerLoanPage />
                </CustomerArea>
              </Route>
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_CUSTOMER_ONGOING)}>
                <CustomerArea>
                  <CustomerLoanPage />
                </CustomerArea>
              </Route>

              {/* Customer Area - SuperSim Shop Page */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_SHOP_PAGE)}>
                <CustomerArea>
                  <CustomerShopPage />
                </CustomerArea>
              </Route>

              <Route
                exact
                path={UrlHelper.getAllRoutes(routes.CUSTOMER_SHOP_PAGE_PROMOTION)}
                component={(props) => (
                  <CustomerArea>
                    <CustomerShopPromotionPage {...props} />
                  </CustomerArea>
                )}
              />

              {/* Customer Area - Insurance page */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_INSURANCE_PAGE)}>
                <CustomerArea>
                  <InsurancePage />
                </CustomerArea>
              </Route>

              {/* Customer Area - Insurance sign page */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_INSURANCE_SIGN_PAGE)}>
                <CustomerArea>
                  <SignInsurance />
                </CustomerArea>
              </Route>

              {/* Customer Area - Configuration */}
              <Route exact path={UrlHelper.getAllRoutes(routes.MESSAGE_PREFERENCE)}>
                <MessageOpsOut />
              </Route>

              {/* Customer Area - Collection by PIX */}
              <Route exact path={UrlHelper.getAllRoutes(routes.PAGE_PIX_PAYMENT)} component={PagePixPayment} />

              <Route exact path={UrlHelper.getAllRoutes(routes.PAGE_LAST_INSTALLMENT_PIX)} component={PagePixPayment} />

              <Route
                exact
                path={[
                  UrlHelper.getAllRoutes(routes.CUSTOMER_LOGIN),
                  UrlHelper.getAllRoutes(routes.CUSTOMER_LOGIN_VARIATION),
                ]}
              >
                <Login />
              </Route>

              <Route path={UrlHelper.getAllRoutes(routes.CUSTOMER_LOGIN_REDIRECT)}>
                <LoginRedirect />
              </Route>

              <Route path={UrlHelper.getAllRoutes(routes.CUSTOMER_LOGIN_LINK)}>
                <SendLoginLink />
              </Route>

              <Route
                exact
                path={[
                  UrlHelper.getAllRoutes(routes.CUSTOMER_REQUEST_PASSWORD_RESET),
                  UrlHelper.getAllRoutes(routes.CUSTOMER_REQUEST_PASSWORD_RESET_VARIATION),
                ]}
              >
                <RequestPasswordReset />
              </Route>

              <Route
                exact
                path={UrlHelper.getAllRoutes(routes.CUSTOMER_PASSWORD_RESET)}
                component={PasswordResetContainer}
              />

              <Route exact path={UrlHelper.getAllRoutes(routes.EVALUATION)}>
                <Evaluation />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.LOADING_PAGE)} component={LoadingPage} />

              <Route exact path={UrlHelper.getAllRoutes(routes.LOADING_PAGE_DISBURSEMENT)} component={LoadingPageDisbursement} />

              {/* Update bank account */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_UPDATE_BANK_ACCOUNT)}>
                <BankInfoUpdateNew />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_BANK_INFO_FAIL)}>
                <BankInfoFail />
              </Route>

              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_BANK_INFO_FAQ)}>
                <BankInfoFaq/>
              </Route>

              {/* Counter offer process interest */}
              <Route exact path={UrlHelper.getAllRoutes(routes.LOAN_COUNTER_OFFER_PROCESS)}>
                <CounterOfferProcess />
              </Route>

              {/* Data Capture App */}
              <Route exact path={[UrlHelper.getAllRoutes(routes.LOAN_APPLICATION_DATA_CAPTURE_APP)]}>
                <DataCaptureAppInstallationPage />
              </Route>

              {/* Update due date */}
              <Route exact path={UrlHelper.getAllRoutes(routes.CUSTOMER_UPDATE_DUE_DATE)}>
                <DueDateUpdate />
              </Route>

              <Route path="*">
                <PageNotFoundComponent />
              </Route>
            </Switch>
          </MainComponent>
        </Suspense>
      </Router>
    </Provider>
  );
}
